import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useShallow } from "zustand/react/shallow";
import { Event, RoomEmote, GameConstant } from "@uno/protocol";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { useWebHaptics } from "web-haptics/react";
import { AppContext } from "context";
import useLocalStorage from "hook/useLocalStorage";
import useStoreRoom from "store/useStoreRoom";

const EmotePicker = React.memo(() => {
    const EMOTE_KEYS = Object.keys(RoomEmote);
    const RADIUS = 75;
    const { socket } = React.useContext(AppContext);
    const { trigger } = useWebHaptics();
    const [hapticEnabled] = useLocalStorage<boolean>("LASTDOTCARDS.HAPTIC", true);
    const roomId = useStoreRoom(useShallow(state => state.room?.id));
    const [isOpen, setIsOpen] = React.useState(false);
    const [cooldown, setCooldown] = React.useState(false);
    const [center, setCenter] = React.useState({ x: 0, y: 0 });
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const handleEmote = React.useCallback((emoteId: string) => {
        if(cooldown || !roomId) {
            return;
        }

        socket?.volatile.emit(Event.RoomPlayerEmote, roomId, emoteId);

        if(hapticEnabled) {
            trigger([
                { duration: 60, intensity: 1 },
                { delay: 80, duration: 40, intensity: 0.5 },
            ]);
        }

        setIsOpen(false);
        setCooldown(true);

        setTimeout(() => setCooldown(false), GameConstant.emoteCooldownSeconds * 1000);
    }, [socket, roomId, cooldown]);

    const handleToggle = React.useCallback(() => {
        if(cooldown) {
            return;
        }

        const player = buttonRef.current?.closest("#player");

        if(player) {
            const rect = player.getBoundingClientRect();
            setCenter({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
        }

        setIsOpen(prev => !prev);
    }, [cooldown]);

    return (
        <>
            <button
                ref={buttonRef}
                onClick={handleToggle}
                className={`absolute z-30 -right-10 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    cooldown ? "bg-white/5 opacity-30 cursor-not-allowed" : "bg-white/10 hover:bg-white/20 cursor-pointer"
                }`}
            >
                <span className="text-base">😊</span>
            </button>

            {ReactDOM.createPortal(
                <AnimatePresence>
                    {isOpen && (
                        <>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-[60]"
                                onClick={() => setIsOpen(false)}
                            />
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{ duration: 0.25, ease: "backOut" }}
                                className="fixed z-[61]"
                                style={{ top: center.y, left: center.x }}
                            >
                                {EMOTE_KEYS.map((key, index) => {
                                    const angle = (index / EMOTE_KEYS.length) * 2 * Math.PI - Math.PI / 2;
                                    const x = Math.cos(angle) * RADIUS;
                                    const y = Math.sin(angle) * RADIUS;

                                    return (
                                        <button
                                            key={key}
                                            onClick={() => handleEmote(key)}
                                            className="absolute w-9 h-9 -ml-[18px] -mt-[18px] rounded-full bg-black/60 flex items-center justify-center hover:scale-125 hover:bg-white/20 transition-all cursor-pointer"
                                            style={{ transform: `translate(${x}px, ${y}px)` }}
                                        >
                                            <span className="text-2xl drop-shadow-md">{RoomEmote[key]}</span>
                                        </button>
                                    );
                                })}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
});

export default EmotePicker;
